/**
 * ImageUploadService — encapsulates attachment upload logic:
 * compression (images only), immediate upload, persistent queue management,
 * and retry. Despite the name it handles any file type — non-image files skip
 * compression and upload as-is.
 *
 * Contract:
 *   - The client picks the attachment id before calling `upload`.
 *   - The final HTTPS URL is derived from `${apiUrl}/attachments/<id>/download`
 *     and is already in the note's markdown when this method runs.
 *   - Retries reuse the same id so the server (which honors client-supplied
 *     ids) treats replays as idempotent.
 *   - Local display before upload completes is handled by the caller via a
 *     URL-keyed blob cache (see `localAttachmentCache.ts`).
 *
 * Legacy queue rows (created before client-id support) still round-trip
 * through `replaceBase64InNoteContent` — they use `base64Src` as the substring
 * to swap once the server returns an id.
 *
 * Does NOT handle editor DOM manipulation.
 * Does NOT own the compressImage utility (calls it as a dependency).
 */

import { compressImage } from '../imageCompression'
import type { AttachmentResponse } from '../attachments'

// ── Storage interface ────────────────────────────────────────────────────────

export interface ImageQueueItem {
  id: number
  noteId: string
  fileName: string
  contentType: string
  fileData: string
  /** Legacy path only — empty for new rows. */
  base64Src: string
  /** New rows: the attachment id (already in markdown via the final URL). */
  attachmentId: string
  /** New rows: the final HTTPS URL already in markdown. */
  finalURL: string
  status: 'pending' | 'uploading' | 'failed'
  retries: number
  createdAt: string
}

export interface ImageQueueStorage {
  enqueue(item: {
    noteId: string
    fileName: string
    contentType: string
    fileData: string
    base64Src: string
    attachmentId: string
    finalURL: string
  }): void
  getPending(): ImageQueueItem[]
  updateStatus(id: number, status: 'pending' | 'uploading'): void
  incrementRetries(id: number): void
  deleteItem(id: number): void
  markFailed(): void
  getNote(id: string): { content: string } | undefined
  updateNoteContent(id: string, content: string): void
}

// ── Upload result types ──────────────────────────────────────────────────────

export type UploadResult =
  | { url: string; id: string }
  | { queued: true }

// ── Service ──────────────────────────────────────────────────────────────────

export class ImageUploadService {
  private _processing = false
  private readonly storage: ImageQueueStorage
  private readonly apiUrl: string
  private readonly maxFileSize: number
  private readonly isOnline: () => boolean
  private readonly isSignedIn: () => boolean

  constructor(opts: {
    storage: ImageQueueStorage
    apiUrl: string
    maxFileSize: number
    isOnline?: () => boolean
    isSignedIn?: () => boolean
  }) {
    this.storage = opts.storage
    this.apiUrl = opts.apiUrl
    this.maxFileSize = opts.maxFileSize
    this.isOnline = opts.isOnline ?? (() => navigator.onLine)
    this.isSignedIn = opts.isSignedIn ?? (() => true)
  }

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * Canonical download URL for an attachment with the given id. Safe to
   * compute before uploading — the worker honors client-supplied ids, so this
   * URL resolves after the upload completes.
   */
  attachmentURL(id: string): string {
    return `${this.apiUrl}/attachments/${id}/download`
  }

  /**
   * Compress and upload an image under a caller-supplied attachment id.
   * If offline or the upload fails, the image is enqueued for later upload
   * via `processQueue()`.
   *
   * The returned URL matches the URL already embedded in markdown.
   *
   * Rejects if the file exceeds `maxFileSize` after compression.
   */
  async upload(
    file: File,
    noteId: string,
    attachmentId: string,
  ): Promise<UploadResult> {
    // Only images are compressed; every other file type uploads as-is.
    const prepared = file.type.startsWith('image/')
      ? await compressImage(file)
      : file

    if (prepared.size > this.maxFileSize) {
      throw new Error(
        `File "${prepared.name}" is ${(prepared.size / 1024 / 1024).toFixed(1)} MB — exceeds ${(this.maxFileSize / 1024 / 1024).toFixed(0)} MB limit`,
      )
    }

    const finalURL = this.attachmentURL(attachmentId)

    // Offline, or no account: `POST /attachments` is auth-gated, so without a
    // session every attempt is a guaranteed 401 that burns a retry. Queue the
    // bytes; the queue drains on sign-in.
    if (!this.isOnline() || !this.isSignedIn()) {
      await this.enqueueForLater(prepared, noteId, attachmentId, finalURL)
      return { queued: true }
    }

    try {
      const result = await this.uploadToServer(prepared, attachmentId)
      return result
    } catch {
      await this.enqueueForLater(prepared, noteId, attachmentId, finalURL)
      return { queued: true }
    }
  }

  /**
   * Process all pending image uploads from the queue.
   * Called during sync cycles and on reconnect.
   */
  async processQueue(): Promise<void> {
    if (!this.isOnline()) return
    if (!this.isSignedIn()) return
    if (this._processing) return

    this._processing = true
    try {
      const items = this.storage.getPending()
      if (items.length === 0) return

      console.debug(
        `[image-upload] Processing ${items.length} queued image upload(s)`,
      )

      for (const item of items) {
        try {
          this.storage.updateStatus(item.id, 'uploading')

          const blob = await base64ToBlob(item.fileData, item.contentType)
          const rawFile = new File([blob], item.fileName, {
            type: item.contentType,
          })
          // Re-compress images only; other file types are uploaded as-is.
          const prepared = item.contentType.startsWith('image/')
            ? await compressImage(rawFile)
            : rawFile

          if (item.attachmentId && item.finalURL) {
            // Canonical path: URL already in markdown, upload under its id.
            await this.uploadToServer(prepared, item.attachmentId)
          } else if (item.base64Src) {
            // Legacy path: server picks the id, swap the data URL out of
            // the note content once we know what it is.
            const result = await this.uploadToServer(prepared)
            this.replaceBase64InNoteContent(
              item.noteId,
              item.base64Src,
              result.url,
            )
          } else {
            this.storage.incrementRetries(item.id)
            continue
          }

          this.storage.deleteItem(item.id)
          console.debug(
            `[image-upload] Successfully uploaded queued image ${item.id} for note ${item.noteId}`,
          )
        } catch (err) {
          console.warn(
            `[image-upload] Failed to process queue item ${item.id}:`,
            err,
          )
          this.storage.incrementRetries(item.id)
        }
      }

      this.storage.markFailed()
    } finally {
      this._processing = false
    }
  }

  getQueueStatus(): { pending: number } {
    const items = this.storage.getPending()
    return { pending: items.length }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private async uploadToServer(
    file: File,
    attachmentId?: string,
  ): Promise<{ url: string; id: string }> {
    const form = new FormData()
    form.append('file', file)
    if (attachmentId) form.append('id', attachmentId)

    const res = await fetch(`${this.apiUrl}/attachments`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })

    if (!res.ok) {
      throw new Error(`Upload failed (${res.status})`)
    }

    const data = (await res.json()) as AttachmentResponse
    const id = data.attachment.id
    return { url: this.attachmentURL(id), id }
  }

  private async enqueueForLater(
    file: File,
    noteId: string,
    attachmentId: string,
    finalURL: string,
  ): Promise<void> {
    const base64Data = await fileToBase64(file)
    this.storage.enqueue({
      noteId,
      fileName: file.name,
      contentType: file.type,
      fileData: base64Data,
      base64Src: '',
      attachmentId,
      finalURL,
    })
    console.debug('[image-upload] Enqueued for later upload:', file.name)
  }

  private replaceBase64InNoteContent(
    noteId: string,
    base64Src: string,
    serverUrl: string,
  ): void {
    const note = this.storage.getNote(noteId)
    if (!note) return

    const updatedContent = note.content.replaceAll(base64Src, serverUrl)
    if (updatedContent !== note.content) {
      this.storage.updateNoteContent(noteId, updatedContent)
    }
  }
}

// ── Standalone helpers ─────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

async function base64ToBlob(
  base64: string,
  contentType: string,
): Promise<Blob> {
  const dataUrl = `data:${contentType};base64,${base64}`
  const res = await fetch(dataUrl)
  return res.blob()
}
